import React from 'react'
import { Table, Image, Tag, Tooltip, Space, Button, Popconfirm } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

interface ContentItem {
    key: string
    id: string
    title: string
    description: string
    category: string
    thumbnail: string
    url: string
    createdAt?: string
    likes?: number
    views?: number
    [key: string]: any // Allow additional properties like bookId
}

interface ContentTableProps<T extends ContentItem> {
    dataSource: T[]
    loading?: boolean
    onView: (record: T) => void
    onEdit: (record: T) => void
    onDelete: (record: T) => void
    selectedRowKeys: React.Key[]
    onSelectionChange: (keys: React.Key[], records: T[]) => void
    type: 'video' | 'audio' | 'book'
}

export const ContentTable = <T extends ContentItem>({
    dataSource,
    loading = false,
    onView,
    onEdit,
    onDelete,
    selectedRowKeys,
    onSelectionChange,
    type,
}: ContentTableProps<T>) => {
    const formatDate = (dateString?: string) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return 'Today'
        if (diffDays === 1) return 'Yesterday'
        if (diffDays < 7) return `${diffDays} days ago`
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
        return date.toLocaleDateString()
    }

    const getTypeLabel = () => {
        switch (type) {
            case 'video':
                return 'video'
            case 'audio':
                return 'audio'
            case 'book':
                return 'book'
            default:
                return 'item'
        }
    }

    const columns: ColumnsType<T> = [
        {
            title: 'Preview',
            dataIndex: 'thumbnail',
            key: 'thumbnail',
            width: 100,
            render: (thumbnail: string) => (
                <Image
                    src={thumbnail}
                    alt="Preview"
                    width={70}
                    height={50}
                    style={{
                        objectFit: 'cover',
                        borderRadius: '4px',
                        background: '#f5f5f5',
                    }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAN0Fu/AAAJ0lEQVR4nO3d"
                    preview={false}
                />
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a: T, b: T) => a.title.localeCompare(b.title),
            render: (text: string, record: T) => (
                <a onClick={() => onView(record)} style={{ fontWeight: 500 }}>
                    {text}
                </a>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={text} placement="topLeft">
                    <span style={{ color: '#666', cursor: 'default' }}>
                        {text?.length > 60 ? `${text.substring(0, 60)}...` : text}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            width: 120,
            sorter: (a: T, b: T) => (a.category || '').localeCompare(b.category || ''),
            render: (category: string) => (
                <Tag color="blue">{category || 'Uncategorized'}</Tag>
            ),
        },
        {
            title: 'Uploaded',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            sorter: (a: T, b: T) =>
                new Date(a.createdAt || 0).getTime() -
                new Date(b.createdAt || 0).getTime(),
            render: (date: string) => (
                <span style={{ color: '#888', fontSize: '13px' }}>{formatDate(date)}</span>
            ),
        },
        {
            title: 'Views',
            dataIndex: 'views',
            key: 'views',
            width: 100,
            sorter: (a: T, b: T) => (a.views || 0) - (b.views || 0),
            render: (views: number) => (
                <Tag color="cyan">{views || 0}</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record: T) => (
                <Space size="small">
                    <Tooltip title="View">
                        <span>
                            <Button
                                type="text"
                                size="small"
                                icon={<EyeOutlined style={{ color: '#52c41a' }} />}
                                onClick={() => onView(record)}
                            />
                        </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <span>
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                onClick={() => onEdit(record)}
                            />
                        </span>
                    </Tooltip>
                    <Popconfirm
                        title={`Delete this ${getTypeLabel()}?`}
                        description="This action cannot be undone."
                        onConfirm={() => onDelete(record)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Delete">
                            <span>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                                />
                            </span>
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectionChange,
    }

    return (
        <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            scroll={{ x: 900 }}
            size="middle"
            style={{ background: '#fff', borderRadius: '8px' }}
        />
    )
}
